/**
 * INP² Auth Service — backend-enforced edition.
 *
 * All authentication and account management runs against the Cloudflare
 * Workers backend. The browser only stores a JWT in localStorage and
 * forwards it as Bearer on every request. This module is a thin client.
 *
 * NO localStorage user store. NO client-side admin creation.
 * Admins are bootstrapped via POST /api/auth/bootstrap-owner exactly once.
 */

const JWT_KEY = 'inp2_jwt';

// Resolve API base — Vite/CRA env, with a deployed default.
const API_BASE = (
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) ||
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  'https://inp2-cyber-bridge-backend.inpsquared.workers.dev'
).replace(/\/$/, '');

// ─── JWT storage ─────────────────────────────────────────────────────────────

export function getJwt() {
  try { return localStorage.getItem(JWT_KEY); } catch { return null; }
}

export function setJwt(token) {
  try { localStorage.setItem(JWT_KEY, token); } catch { /* ignore */ }
}

export function clearJwt() {
  try { localStorage.removeItem(JWT_KEY); } catch { /* ignore */ }
}

export function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isJwtExpired(token) {
  const p = decodeJwtPayload(token);
  if (!p || !p.exp) return true;
  return Math.floor(Date.now() / 1000) >= p.exp;
}

// ─── HTTP helpers ────────────────────────────────────────────────────────────

async function apiCall(path, { method = 'GET', body, auth = true } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const token = getJwt();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    return { ok: false, status: 0, error: 'Network error', raw: null };
  }
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON */ }
  return {
    ok: res.ok,
    status: res.status,
    error: res.ok ? null : (json?.error || `HTTP ${res.status}`),
    data: json?.data ?? json,
    raw: json,
  };
}

export function apiBase() { return API_BASE; }
export function authedFetch(path, init = {}) {
  const headers = new Headers(init.headers || {});
  const token = getJwt();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

// ─── Auth flows ──────────────────────────────────────────────────────────────

/**
 * Login by email or username (DEMO accounts use username='DEMO').
 * Returns { ok, user?, error?, locked? }
 */
export async function login(usernameOrEmail, password) {
  const isEmail = usernameOrEmail.includes('@');
  const body = isEmail
    ? { email: usernameOrEmail.trim().toLowerCase(), password }
    : { username: usernameOrEmail.trim(), password };

  const res = await apiCall('/api/auth/login', { method: 'POST', body, auth: false });
  if (res.status === 423) {
    return { ok: false, locked: true, error: res.error || 'Account temporarily locked. Try again later.' };
  }
  if (!res.ok) {
    return { ok: false, error: res.error || 'Invalid credentials' };
  }
  const { token, user } = res.data || {};
  if (!token || !user) return { ok: false, error: 'Malformed login response' };
  setJwt(token);
  return { ok: true, user };
}

export async function logout() {
  // Best-effort server notification, but the JWT being gone is what matters.
  try { await apiCall('/api/auth/logout', { method: 'POST' }); } catch { /* ignore */ }
  clearJwt();
}

/**
 * Fetch the current user from /api/auth/me.
 * Returns the user object or null if the JWT is missing/invalid.
 */
export async function fetchCurrentUser() {
  const token = getJwt();
  if (!token || isJwtExpired(token)) {
    clearJwt();
    return null;
  }
  const res = await apiCall('/api/auth/me');
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) clearJwt();
    return null;
  }
  return res.data || null;
}

// ─── Admin operations (super_admin only — server enforces) ───────────────────

export async function adminListUsers() {
  return apiCall('/api/admin/users');
}

export async function adminListTenants() {
  return apiCall('/api/admin/tenants');
}

export async function adminListAuditLog({ limit = 100, before, action } = {}) {
  const qs = new URLSearchParams();
  if (limit) qs.set('limit', String(limit));
  if (before) qs.set('before', String(before));
  if (action) qs.set('action', action);
  return apiCall(`/api/admin/audit-log?${qs.toString()}`);
}

export async function adminCreateCustomer(payload) {
  return apiCall('/api/admin/customers', { method: 'POST', body: payload });
}

export async function adminCreateDemo(payload) {
  return apiCall('/api/admin/demos', { method: 'POST', body: payload });
}

export async function adminSuspendUser(userId) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

export async function adminRestoreUser(userId) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}/restore`, { method: 'POST' });
}

export async function adminResetPassword(userId) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, { method: 'POST' });
}

export async function adminUpdateSubscription(userId, status) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}/subscription`, {
    method: 'PATCH',
    body: { status },
  });
}

export async function changeOwnPassword(userId, oldPassword, newPassword) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    body: { oldPassword, newPassword },
  });
}

export async function adminDeleteUser(userId) {
  return apiCall(`/api/admin/users/${encodeURIComponent(userId)}/permanent`, { method: 'DELETE' });
}

export async function adminDeleteTenant(tenantId) {
  return apiCall(`/api/admin/tenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' });
}
